/**
 * Utility functions to check if a user needs to complete onboarding
 */

/**
 * Checks if a user is in the middle of onboarding and needs to complete it
 * @param {Object} userInfo - The user info object from Redux state
 * @returns {boolean} - True if user needs to complete onboarding
 */
export const needsOnboardingCompletion = (userInfo) => {
  if (!userInfo) return false;

  const {
    organization,
    billing,
    id: userId,
    isCancel,
    personas,
  } = userInfo;

  const { planningSubscription: subscription, hasPaymentMethod } = billing || {};

  // If user is a client/vendor skip account set up
  if (personas?.isClientVendor) return false;

  // User must be the owner of an organization
  const isOrgOwner = userId === organization?.ownerId;
  if (!isOrgOwner) return false;

  // Don't force onboarding for canceled users - they should go to billing to resubscribe
  if (isCancel) return false;

  // If user has payment method, they don't need to go through onboarding.
  if (hasPaymentMethod) return false;

  // If billing data is not available yet, don't force onboarding
  if (billing === undefined) {
    return false;
  }

  // If user has ANY subscription record (even ended/expired), they're a planning partner
  // and should be treated as a regular member, not forced through onboarding
  if (subscription && subscription.id) {
    return false;
  }

  // If user has no subscription at all, they need to complete onboarding
  return true;
};

/**
 * Checks if the current path is part of the onboarding flow
 * @param {string} pathname - The current pathname
 * @returns {boolean} - True if user is in onboarding flow
 */
export const isOnboardingPath = (pathname) => {
  const onboardingPaths = [
    '/app/v2/account-setup',
    '/app/v2/account-setup/step/',
    '/app/v2/account-setup/get-started',
  ];

  return onboardingPaths.some(path => pathname.includes(path));
};

/**
 * Gets the appropriate onboarding step for the user
 * @param {Object} userInfo - The user info object from Redux state
 * @returns {string} - The onboarding step URL
 */
export const getOnboardingStepUrl = (userInfo) => {
  if (!userInfo) return '/app/v2/account-setup/get-started';

  // For now, always start at step 1
  // In the future, you could implement logic to determine which step to resume from
  // based on what data has already been completed
  return '/app/v2/account-setup/step/1';
};
